'use client';

import { useIsMobile } from '@shared/hooks/useMobile';
import type { AdvancedFilterProps } from './types';
import AdvancedFilterDesktop from './AdvancedFilterDesktop';
import AdvancedFilterMobile from './AdvancedFilterMobile';

/**
 * Componente AdvancedFilter - Orquestador principal del sistema de filtros avanzados
 *
 * Componente inteligente que decide automáticamente entre la versión desktop o mobile
 * basándose en el tamaño de pantalla del dispositivo.
 *
 * Características:
 * - Detección automática de dispositivo (mobile/desktop)
 * - Renderizado condicional del componente apropiado
 * - Mantiene la misma interfaz pública para ambos casos
 * - Cero cambios requeridos en páginas existentes
 * - Optimización automática según el contexto de uso
 *
 * @param filterOptions - Configuración de columnas, acciones y valores disponibles
 * @param onChange - Callback ejecutado cuando cambian las condiciones válidas
 * @param initialConditions - Condiciones iniciales para hidratar el estado
 * @param translations - Textos traducidos opcionales para override específico
 */
const AdvancedFilter = ({
  filterOptions,
  onChange,
  initialConditions,
  translations,
}: AdvancedFilterProps) => {
  // 🎯 Detectar si estamos en mobile (pantallas menores a 768px)
  const isMobile = useIsMobile();

  // 🎯 Renderizar el componente apropiado según el dispositivo
  return isMobile ? (
    <AdvancedFilterMobile
      filterOptions={filterOptions}
      onChange={onChange}
      initialConditions={initialConditions}
      translations={translations}
    />
  ) : (
    <AdvancedFilterDesktop
      filterOptions={filterOptions}
      onChange={onChange}
      initialConditions={initialConditions}
      translations={translations}
    />
  );
};

export default AdvancedFilter;
